import React, { useMemo } from 'react';

import Modal from '../../../../../shared/design-system/ui/modal';
import Button from '../../../../../shared/design-system/ui/button';

import { supportUrls } from '../../../../../shared/utils/urls';
import { PhoneNumberCartItem } from '../../../types/phone-numbers';

interface PhoneNumberBillingSummaryModalProps {
  show: boolean;
  onSubmit: () => void;
  onClose: () => void;
  isLoading: boolean;
  totalPhoneNumbers: number;
  totalAmount: string;
  phoneNumbersInCart: PhoneNumberCartItem[];
}

const PhoneNumberBillingSummaryModal: React.FC<
  PhoneNumberBillingSummaryModalProps
> = ({
  show,
  onSubmit,
  onClose,
  isLoading,
  totalPhoneNumbers,
  totalAmount,
  phoneNumbersInCart,
}) => {
  const billingDetails = useMemo(() => {
    const freeItems = phoneNumbersInCart.filter((item) => item.isFreeNumber);
    if (freeItems.length === 0) {
      return null;
    }

    // Parse the total amount string (format: "$15.00/quarter")
    const totalAmountValue = parseFloat(
      totalAmount.replace('$', '').replace('/quarter', ''),
    );
    const freeNumbersApplied = freeItems.length;
    const chargeableNumbers = totalPhoneNumbers - freeNumbersApplied;

    // Sum actual prices of free numbers for discount
    const discountAmount = freeItems.reduce((sum, item) => {
      const price = parseFloat(item.pricePerQuarter.replace('$', ''));
      return sum + (Number.isNaN(price) ? 0 : price);
    }, 0);
    const finalAmount = totalAmountValue - discountAmount;

    return {
      freeNumbersApplied,
      chargeableNumbers,
      subtotal: `$${totalAmountValue.toFixed(2)}`,
      discount: `-$${discountAmount.toFixed(2)}`,
      totalToPay: `$${finalAmount.toFixed(2)}`,
    };
  }, [phoneNumbersInCart, totalPhoneNumbers, totalAmount]);

  const hasFreeNumbers = billingDetails !== null;

  return (
    <Modal
      show={show}
      className="phone-number-billing-summary-modal"
      title={
        <div className="d-flex align-items-center gap-10">
          <span className="line-height-1">Billing Summary</span>
        </div>
      }
      onHide={onClose}
      showCloseButton={true}
      hideFooter={true}
    >
      <div className="billing-summary">
        <div className="pricing">
          {hasFreeNumbers && billingDetails ? (
            <>
              <div className="price-details">
                <p className="section-label">Number Breakdown</p>
                <div className="price-list">
                  <div className="price-item">
                    <p className="label">Total phone number purchased</p>
                    <p className="value">{totalPhoneNumbers}</p>
                  </div>
                  <div className="price-item">
                    <p className="label">Free numbers (Eligible)</p>
                    <p className="value">
                      {billingDetails.freeNumbersApplied}
                    </p>
                  </div>
                  <div className="price-item">
                    <p className="label">Total chargeable phone numbers</p>
                    <p className="value">
                      {billingDetails.chargeableNumbers}
                    </p>
                  </div>
                </div>
                <div className="price-details-divider" />
              </div>

              <div className="price-details">
                <p className="section-label">Cost Calculation</p>
                <div className="price-list">
                  <div className="price-item">
                    <p className="label">Subtotal (before discount)</p>
                    <p className="value">{billingDetails.subtotal}</p>
                  </div>
                  <div className="price-item">
                    <p className="label">
                      Discount applied ({billingDetails.freeNumbersApplied}{' '}
                      free phone{' '}
                      {billingDetails.freeNumbersApplied === 1
                        ? 'number'
                        : 'numbers'}
                      )
                    </p>
                    <p className="value discount-value">
                      {billingDetails.discount}
                    </p>
                  </div>
                </div>
                <div className="price-details-divider" />
              </div>

              <div className="price-details">
                <div className="price-list">
                  <div className="price-item">
                    <p className="label total-label">Total amount to pay</p>
                    <p className="value">{billingDetails.totalToPay}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="price-details">
              <div className="price-list">
                <div className="price-item">
                  <p className="label">Total phone number purchased</p>
                  <p className="value">{totalPhoneNumbers}</p>
                </div>
                <div className="price-item">
                  <p className="label">Total amount to pay</p>
                  <p className="value">{totalAmount}</p>
                </div>
              </div>
              <div className="price-details-divider" />
            </div>
          )}

          <div className="total-section">
            <div className="policy-text">
              By purchasing, you agree to our{' '}
              <a
                href={supportUrls.refundPolicy}
                target="_blank"
                rel="noreferrer"
                className="refund-policy-link"
              >
                refund policy
              </a>
              .
            </div>
          </div>
        </div>

        <div className="footer">
          <ul className="policy-list">
            <li>Phone Numbers auto-renew quarterly.</li>
            <li>Mid-quarter deletions are non-refundable.</li>
          </ul>

          <Button
            className="pay-now"
            onClick={onSubmit}
            isLoading={isLoading}
            disabled={isLoading}
            loadingText="Processing..."
          >
            Pay Now
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PhoneNumberBillingSummaryModal;
